import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { CommissionTrackerService } from '../../services';

/**
 * Commission Integration Component
 * Provides a bridge between the Match Dashboard and Commission Tracking
 * This implements the updateCommissionTracker() functionality from your Apps Script
 */
const CommissionIntegration = ({ 
  matchResults, 
  sessionId, 
  onCommissionGenerated 
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Count ready matches - check for both 'Ready' and 'Ready for Commission' formats
  const readyMatches = matchResults?.filter(match => 
    match.action === 'Ready' || match.action === 'Ready for Commission'
  ) || [];
  const readyCount = readyMatches.length;

  // Calculate commission totals
  const totalRepCommission = readyCount * 90; // $90 per deal
  const totalManagerCommission = readyCount * 10; // $10 per deal
  const totalCommission = totalRepCommission + totalManagerCommission;

  // Get rep breakdown - check multiple possible locations for rep information
  const repBreakdown = readyMatches.reduce((acc, match) => {
    const repName = match.hubspotSnapshot?.insideSalesRep || 
                   match.hubspotRecord?.insideSalesRep || 
                   match.hubspotRecord?.salesRep ||
                   'Unknown';
    acc[repName] = (acc[repName] || 0) + 1;
    return acc;
  }, {});

  const handleGenerateCommissions = async () => {
    if (!sessionId) {
      setError('No session ID available. Please run matching first.');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await CommissionTrackerService.generateCommissionTracker(sessionId);
      
      setSuccess(`Successfully generated ${result.data.recordsCreated} commission records!`);
      
      // Notify parent component
      if (onCommissionGenerated) {
        onCommissionGenerated(result);
      }

      // Close dialog after a brief delay
      setTimeout(() => {
        setDialogOpen(false);
        setSuccess(null);
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (amount) => CommissionTrackerService.formatCurrency(amount);

  // Don't show button if no ready matches
  if (readyCount === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<MoneyIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ 
          backgroundColor: '#000',
          color: '#fff',
          borderRadius: 3,
          height: 48,
          paddingLeft: 3,
          paddingRight: 3,
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: '#333',
            boxShadow: '0 6px 16px rgba(0,0,0,0.4)',
          }
        }}
      >
        Generate Commission ({readyCount} deals)
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={() => !generating && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MoneyIcon sx={{ mr: 1, color: 'success.main' }} />
            Generate Commission Tracker
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Typography variant="body1" sx={{ mb: 3 }}>
            This will create commission records for all deals marked as &quot;Ready for Commission&quot;. 
            Review the summary below:
          </Typography>

          {/* Commission Summary */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
              Commission Summary
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Total Deals Ready:</Typography>
              <Typography fontWeight="bold">{readyCount}</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Rep Commission:</Typography>
              <Typography fontWeight="bold" color="success.main">
                {formatCurrency(totalRepCommission)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Manager Commission:</Typography>
              <Typography fontWeight="bold" color="info.main">
                {formatCurrency(totalManagerCommission)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6">Total Commission:</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(totalCommission)}
              </Typography>
            </Box>
          </Box>

          {/* Rep Breakdown */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Rep Breakdown
          </Typography>
          
          <List dense>
            {Object.entries(repBreakdown).map(([repName, count]) => (
              <ListItem key={repName}>
                <ListItemIcon>
                  <CheckIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={repName}
                  secondary={`${count} deals • ${formatCurrency(count * 90)} commission`}
                />
              </ListItem>
            ))}
          </List>

          {/* Manager Commission Note */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <InfoIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              <strong>Manager Commission:</strong> Tyson Queen will receive {formatCurrency(totalManagerCommission)} 
              manager commission ({formatCurrency(10)} per deal) plus his individual rep commission 
              for any deals he personally closed.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setDialogOpen(false)}
            disabled={generating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerateCommissions}
            variant="contained"
            color="success"
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : <MoneyIcon />}
          >
            {generating ? 'Generating...' : 'Generate Commission Records'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommissionIntegration;
